import React from 'react'
import { Route, Switch, useHistory } from 'react-router'

import { useCheckout } from '../../state'
import {
  DocumentStep,
  NameStep,
  CompanyNameStep,
  TradeNameStep,
  CorporatePhoneStep,
  CorporateDocumentStep,
  ClientProfileContainer,
  ClientProfileDataByEmaillStep,
  PhoneStep,
  StateInscriptionStep,
  IdentificationStep,
} from '../../components'
import { CheckoutRoute, PURCHASE_FLOW_ORIGIN_HASH } from '../../constants'
import { isEditMode } from '../../utils/verifyEditionMode'
import { getOrigin } from '../../utils/getOrigin'
import type { ClientDataState } from '../../typings'
import type { OrderForm } from '../../../../core/requests/checkout'
import { useCustomConfig } from '../../../../core/hooks/useCustomConfig'
import { isClientProfileComplete } from '../../utils'
import { getClientDataStateFromOrderForm } from '../../utils/getClientDataStateFromOrderForm'
import { checkNameIsAnonymous } from '../../utils/checkNameIsAnonymous'
import { TopbarMenuArea } from '../../../Instore/components/Header/Topbar'
import { BackButton } from '../../../../core/components'
import { useIdentificationStep } from '../../hooks'

export const TakeAwayScreen = () => {
  const history = useHistory()
  const isEditing = isEditMode()
  const origin = getOrigin() ?? ''
  const checkout = useCheckout()
  const clientDataState = checkout.checkout.clientData

  const { allowAnonymousUser } = useCustomConfig()
  const isEmailRequired = !clientDataState.canSkipEmail || !allowAnonymousUser
  const initialName = checkNameIsAnonymous(clientDataState.name)
    ? ''
    : clientDataState.name

  const identificationStep = useIdentificationStep()

  const handleEmailSubmit = async (submittedOrderForm: OrderForm | null) => {
    const hasSkippedEmail = !submittedOrderForm

    if (hasSkippedEmail) {
      return history.push(CheckoutRoute.OrderSummary)
    }

    checkout.setClientDataCanSkipEmail(false)

    if (
      submittedOrderForm &&
      isClientProfileComplete(submittedOrderForm.clientProfileData)
    ) {
      checkout.setClientDataState(
        getClientDataStateFromOrderForm(submittedOrderForm)
      )

      return history.push(CheckoutRoute.OrderSummary)
    }

    checkout.setClientDataEmail(
      submittedOrderForm?.clientProfileData?.email ?? ''
    )

    if (submittedOrderForm?.clientProfileData?.document) {
      history.push(CheckoutRoute.TakeAwayName)
    } else {
      history.push(CheckoutRoute.TakeAwayDocument)
    }
  }

  const handleNameSubmit = (name: ClientDataState['name']) => {
    checkout.setClientDataName(name)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.TakeAwayPhone
    )
  }

  const handleDocumentSubmit = (document: ClientDataState['document']) => {
    checkout.setClientDataDocument(document)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.TakeAwayName
    )
  }

  const handlePhoneSubmit = (phone: ClientDataState['phone']) => {
    checkout.setClientDataPhone(phone)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.TakeAwayClientProfile
    )
  }

  const handleConfirmClientSubmit = () => {
    history.push('../order-summary')
  }

  const handleCompanyNameSubmit = (
    companyName: ClientDataState['companyInformation']['companyName']
  ) => {
    checkout.setClientDataCompanyName(companyName)

    history.push(
      isEditing
        ? CheckoutRoute.TakeAwayClientProfile
        : CheckoutRoute.TakeAwayTradeName
    )
  }

  const handleTradeNameSubmit = (
    tradeName: ClientDataState['companyInformation']['tradeName']
  ) => {
    checkout.setClientDataTradeName(tradeName)

    history.push(
      isEditing
        ? CheckoutRoute.TakeAwayClientProfile
        : CheckoutRoute.TakeAwayCorporatePhone
    )
  }

  const handleCorporatePhoneSubmit = (
    corporatePhone: ClientDataState['companyInformation']['corporatePhone']
  ) => {
    checkout.setClientDataCorporatePhone(corporatePhone)

    history.push(
      isEditing
        ? CheckoutRoute.TakeAwayClientProfile
        : CheckoutRoute.TakeAwayCorporateDocument
    )
  }

  const handleCorporateDocumentSubmit = (
    corporateDocument: ClientDataState['companyInformation']['corporateDocument']
  ) => {
    checkout.setClientDataCorporateDocument(corporateDocument)

    history.push(
      isEditing
        ? CheckoutRoute.TakeAwayClientProfile
        : CheckoutRoute.TakeAwayStateInscription
    )
  }

  const handleStateInscriptionSubmit = (
    stateInscription: ClientDataState['companyInformation']['stateInscription']
  ) => {
    checkout.setClientDataStateIncription(stateInscription)
    checkout.setClientDataIsCorporate(true)

    history.push(CheckoutRoute.TakeAwayClientProfile)
  }

  const handleIdentifySubmit = (
    submittedOrderForm: OrderForm | null,
    identification: string | null,
    identificationType: string
  ) => {
    identificationStep.handleStepSubmit(
      submittedOrderForm,
      identification,
      identificationType,
      {
        document: CheckoutRoute.TakeAwayDocument,
        email: CheckoutRoute.TakeAwayEmail,
        afterIdentification: CheckoutRoute.OrderSummary,
      }
    )
  }

  return (
    <>
      <TopbarMenuArea>
        <BackButton />
      </TopbarMenuArea>
      <Switch>
        <Route path={CheckoutRoute.TakeAwayIdentify}>
          <IdentificationStep
            onStepSubmit={handleIdentifySubmit}
            initialIdentification={clientDataState.identification}
            isRequired={isEmailRequired}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayEmail}>
          <ClientProfileDataByEmaillStep
            onStepSubmit={handleEmailSubmit}
            isRequired={isEmailRequired}
            initialEmail={clientDataState.email}
            initialDocument={clientDataState.document}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayName}>
          <NameStep
            isEditing={isEditing}
            onStepSubmit={handleNameSubmit}
            initialName={initialName}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayDocument}>
          <DocumentStep
            isEditing={isEditing}
            onStepSubmit={handleDocumentSubmit}
            initialDocument={clientDataState.document}
            clientName={clientDataState.name}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayPhone}>
          <PhoneStep
            isEditing={isEditing}
            onStepSubmit={handlePhoneSubmit}
            clientName={clientDataState.name}
            initialPhone={clientDataState.phone}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayClientProfile}>
          <ClientProfileContainer nextStep={handleConfirmClientSubmit} />
        </Route>
        <Route path={CheckoutRoute.TakeAwayCompanyName}>
          <CompanyNameStep
            isEditing={isEditing}
            data-testid="company-name-pickup-step"
            onStepSubmit={handleCompanyNameSubmit}
            initialName={clientDataState.companyInformation?.companyName}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayTradeName}>
          <TradeNameStep
            isEditing={isEditing}
            data-testid="trade-name-pickup-step"
            onStepSubmit={handleTradeNameSubmit}
            initialName={clientDataState.companyInformation?.tradeName}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayCorporatePhone}>
          <CorporatePhoneStep
            isEditing={isEditing}
            onStepSubmit={handleCorporatePhoneSubmit}
            initialPhone={clientDataState.companyInformation?.corporatePhone}
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayCorporateDocument}>
          <CorporateDocumentStep
            isEditing={isEditing}
            onStepSubmit={handleCorporateDocumentSubmit}
            initialDocument={
              clientDataState.companyInformation?.corporateDocument
            }
          />
        </Route>
        <Route path={CheckoutRoute.TakeAwayStateInscription}>
          <StateInscriptionStep
            isEdit={isEditing}
            onStepSubmit={handleStateInscriptionSubmit}
            initialStateInscription={
              clientDataState.companyInformation.stateInscription
            }
          />
        </Route>
      </Switch>
    </>
  )
}
